import React, { useContext, useState, FunctionComponent, PropsWithChildren, useRef, useEffect } from 'react'
import Moment from 'react-moment'
import { Auth0Context, useAuth0 } from '../contexts/auth0'
import { bindContexts } from '../utils/bindContexts'
import 'src/assets/css/components/jobRow.css'
import calendarIcon from '../assets/images/svgs/calendar.svg';
import scheduleIcon from '../assets/images/svgs/schedule.svg';
import playIcon from '../assets/images/svgs/play.svg';
import deleteIcon from '../assets/images/svgs/delete.svg'
import downloadIcon from '../assets/images/svgs/download-icon.svg'
import { IJobRowProps } from 'src/models/components/IJobRow'
import ndjsonStream from 'src/utils/stream/ndjson-stream'
import stringStream from 'src/utils/stream/string-stream'
import { Button, IconButton, ListItemIcon, Menu, MenuItem } from '@material-ui/core'
import { ExpandMore, FileCopyOutlined, MoreVert } from '@material-ui/icons'
import sparkleIcon from '../assets/images/svgs/sparkle.svg'
import { WindowContext } from 'src/contexts/window';
import { logError } from 'src/utils/Error'
import Spinner from 'src/components/Spinner'
import useRowStyles from 'src/assets/css/components/RowComponentsStyles'
import IJob, { JobStatus } from 'src/models/IJob'
import ILog from 'src/models/ILog'
import { canDownloadLogs, LOGS_DOWNLOAD_UNAVAILABLE_MESSAGE, statusLabel } from 'src/utils/jobStatus'


// number of log lines the viewer initially requests (the full log remains
// available via download)
export const DEFAULT_LOG_TAIL = 1000

const JobRow: FunctionComponent<IJobRowProps> = (props: PropsWithChildren<IJobRowProps>) => {
  const classes = useRowStyles();
  const { size } = useContext(WindowContext)
  const { job, jobMarker, renderLogs, openLog, handleOpenJobConfirmDialog, downloadLog, onDiagnose, onCopyPrompt, onPrepare } = props
  const { getTokenSilently } = useAuth0()
  const [isDownloading, setIsDownloading] = useState(false)
  const [state, setState] = useState({
    isMenuOpen: false,
    menuAnchorEl: null as null | EventTarget & HTMLButtonElement,
  })
  // MEL-574 per-run diagnosis: a "Diagnose" dropdown is offered only on a failed run, and only
  // when the parent wired the handlers (i.e. this pipeline/workspace can be diagnosed).
  // WARNING counts (MEL-568): the attempt did fail, and a user asking why is not the same as an
  // alert pushed at them — only the unprompted notification waits for the final attempt.
  const canDiagnose = [JobStatus.Error, JobStatus.Warning].includes(job.status)
    && !!onDiagnose && !!onCopyPrompt
  const [diagnoseAnchorEl, setDiagnoseAnchorEl] = useState<null | (EventTarget & HTMLButtonElement)>(null)
  const openDiagnoseMenu = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setDiagnoseAnchorEl(e.currentTarget)
    // pre-fetch the prompt now, while the user reads the two-item menu, so a subsequent
    // "Copy diagnosis prompt" click resolves instantly instead of a cold few-second wait
    onPrepare?.(job)
  }
  const closeDiagnoseMenu = () => setDiagnoseAnchorEl(null)
  const canDownload = canDownloadLogs(job.status)
  const jobRef = useRef(job)
  const openLogsOnMenuExitRef = useRef(false)

  // Close the diagnose dropdown on scroll. With body scroll-lock disabled (so the page stays
  // scrollable behind the menu), MUI v4's popover positions once at open time and no longer
  // follows its anchor — so on scroll it would float detached from the run row. Closing on
  // scroll is the conventional menu behaviour and keeps it from ever looking misplaced (MEL-574).
  // Capture phase catches scroll on any ancestor container, not just the window.
  useEffect(() => {
    if (!diagnoseAnchorEl) return
    const close = () => setDiagnoseAnchorEl(null)
    window.addEventListener('scroll', close, true)
    return () => window.removeEventListener('scroll', close, true)
  }, [diagnoseAnchorEl])

  useEffect(() => {
    jobRef.current = job
  }, [job])

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => setState({ ...state, isMenuOpen: true, menuAnchorEl: e.currentTarget })
  const handleCloseMenu = () => setState({ ...state, isMenuOpen: false })

  const getLog = async (prevJob: IJob, sequence?: string) => {
    const job = jobRef.current
    const logsHref = new URL(job._links.logs.href)

    openLog(job)

    if (sequence !== undefined)
      logsHref.searchParams.set('sequence', sequence)
    else
      logsHref.searchParams.set('tail', String(DEFAULT_LOG_TAIL))

    try {
      const token = await getTokenSilently()

      await fetch(logsHref.toString(), {
        headers: {
          Accept: 'application/stream+json',
          Authorization: `Bearer ${token}`
        }
      })
        .then(async (response) => {
          if (response.status == 200) {
            if (typeof response.body !== 'undefined' && response.body != null) {
              return ndjsonStream(response.body)
            } else {
              const body = await response.text()
              return ndjsonStream(stringStream(body))
            }
          }
          return null
        })
        .then(async (jsonStream: ReadableStream<ILog> | null) => {
          if (!jsonStream)
            return

          const reader = jsonStream.getReader()
          const logs: ILog[] = []

          while (true) {
            const result = await reader.read()

            if (!result.done) {
              logs.push(result.value)
              continue
            }

            const promiseId = ([JobStatus.Queued, JobStatus.Running].includes(job.status) || job.status !== prevJob?.status)
              ? setTimeout(getLog, 5000, job, logs[logs.length - 1]?.sequence)
              : null

            return renderLogs(logs, promiseId)
          }
        })
        .catch(logError)
    } catch (e) {
      logError(e)
    }
  }

  const downloadLogs = async () => {
    setState({ ...state, isMenuOpen: false })
    setIsDownloading(true)
    await downloadLog(job)
    setIsDownloading(false)
  }

  const showLogs = () => {
    if (!state.isMenuOpen) {
      getLog(job)
      return
    }

    setState({ ...state, isMenuOpen: false })
    openLogsOnMenuExitRef.current = true
  }

  const deleteJob = () => {
    setState({ ...state, isMenuOpen: false })
    handleOpenJobConfirmDialog(job)
  }

  const handleDiagnoseWithClaude = () => {
    closeDiagnoseMenu()
    onDiagnose?.(job)
  }

  const handleCopyPrompt = () => {
    closeDiagnoseMenu()
    onCopyPrompt?.(job)
  }

  // shared dropdown menu for the two run-level diagnose actions (desktop button + mobile menu)
  const diagnoseMenu = (
    <Menu
      data-testid="diagnose-menu"
      className={classes.dropdownMenu}
      // MUI locks body scroll while a Menu is open by default, which freezes the whole page
      // behind this small dropdown — disable it so the page stays scrollable (MEL-574)
      disableScrollLock
      open={!!diagnoseAnchorEl}
      anchorEl={diagnoseAnchorEl}
      getContentAnchorEl={null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClose={closeDiagnoseMenu}>
      <MenuItem className={classes.dropdownItem} dense data-testid="diagnose-copy-prompt" onClick={handleCopyPrompt}>
        <ListItemIcon style={{ minWidth: 28 }}>
          <FileCopyOutlined fontSize="small" />
        </ListItemIcon>
        <span>Copy diagnosis prompt</span>
      </MenuItem>
      <MenuItem className={classes.dropdownItem} dense data-testid="diagnose-with-claude" onClick={handleDiagnoseWithClaude}>
        <ListItemIcon style={{ minWidth: 28 }}>
          <img src={sparkleIcon} alt="" style={{ width: 14, height: 13 }} />
        </ListItemIcon>
        <span>Diagnose with Claude…</span>
      </MenuItem>
    </Menu>
  )

  const diagnoseButton = (
    <Button
      size="small"
      variant="outlined"
      data-testid="diagnose-button"
      endIcon={<ExpandMore />}
      onClick={openDiagnoseMenu}
      title="Diagnose this failed run with Melty AI"
      style={{ textTransform: 'none', color: '#7C56DC', borderColor: '#E0D7F5' }}>
      Diagnose
    </Button>
  )

  return (
        <div
          id={job.id}
          className={'vertical-center data-job-row'}>
            <div className={'vertical-center info'}>
              <span>
                <img src={calendarIcon} alt="calendar icon" />
                <Moment format="YYYY-MM-DD HH:mm" date={job.created} utc={true} local={true} />
              </span>
              <span>
                <img src={playIcon} alt="play icon" />
                {`by ${job.attempt
                  ? `retry (attempt ${job.attempt}/${job.maxAttempts})`
                  : job.triggeredBy
                  || job._embedded.profile?.email
                  || "schedule"}`}
              </span>
              <span className={`vertical-center status circle ${jobMarker(job.status)}`}>
                {statusLabel(job.status)}
              </span>
              <span>
                <img src={scheduleIcon} alt="schedule icon" />
                {job.startTime && job.endTime
                  ? <Moment from={job.startTime}>{job.endTime}</Moment>
                  : <Moment interval={1000} date={job.startTime} utc={true} durationFromNow={true} trim={true} format="h[h]m[m]s[s] [elapsed]" />}
              </span>
            </div>
            <div className="d-flex job-actions">
              {canDiagnose && diagnoseMenu}
              {!size.smallScreen ? (
                <>
                  {canDiagnose && diagnoseButton}
                  {job._links.logs && <>
                    <span
                      className="logs-button"
                      onClick={showLogs}>
                        View logs
                    </span>
                    <div className="button-wrapper"
                      style={canDownload ? { cursor: 'pointer' } : { cursor: 'not-allowed', opacity: 0.4 }}
                      title={canDownload ? 'Download logs' : LOGS_DOWNLOAD_UNAVAILABLE_MESSAGE}
                      onClick={canDownload ? downloadLogs : undefined}>
                      <Spinner active={isDownloading}>
                        <img src={downloadIcon}/>
                      </Spinner>
                    </div>
                  </>}
                  {job._links['delete job'] &&
                    <div className="button-wrapper"
                      style={{ cursor: 'pointer' }}
                      onClick={deleteJob}
                      title="Delete job">
                      <img className="delete-job" src={deleteIcon} />
                    </div>}
                </>
              ) : (
                <>
                  <Menu
                    data-testid="context-menu"
                    className={classes.dropdownMenu}
                    open={state.isMenuOpen}
                    anchorEl={state.menuAnchorEl}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    onClose={handleCloseMenu}
                    TransitionProps={{
                      onExit: () => {
                        if (openLogsOnMenuExitRef.current)
                          getLog(job)

                        openLogsOnMenuExitRef.current = false
                      }
                    }}>
                    {canDiagnose &&
                      <MenuItem
                        className={classes.dropdownItem}
                        dense
                        data-testid="diagnose-copy-prompt-mobile"
                        onClick={() => { setState({ ...state, isMenuOpen: false }); onCopyPrompt?.(job) }}>
                        <FileCopyOutlined fontSize="small" style={{ marginRight: 8 }} />
                        <span>Copy diagnosis prompt</span>
                      </MenuItem>}
                    {canDiagnose &&
                      <MenuItem
                        className={classes.dropdownItem}
                        dense
                        data-testid="diagnose-with-claude-mobile"
                        onClick={() => { setState({ ...state, isMenuOpen: false }); onDiagnose?.(job) }}>
                        <img src={sparkleIcon} alt="" style={{ width: 14, height: 13, marginRight: 8 }} />
                        <span>Diagnose with Claude…</span>
                      </MenuItem>}
                    {job._links.logs &&
                      <MenuItem
                        className={classes.dropdownItem}
                        dense
                        disabled={!canDownload}
                        title={canDownload ? undefined : LOGS_DOWNLOAD_UNAVAILABLE_MESSAGE}
                        onClick={downloadLogs}>
                        <i className='fas fa-download' />
                        <span>{canDownload ? 'Download' : 'Download (once the run starts)'}</span>
                      </MenuItem>}
                    {job._links.logs &&
                      <MenuItem
                        className={classes.dropdownItem}
                        dense
                        onClick={showLogs}>
                        <i className='fas fa-terminal' />
                        <span>Logs</span>
                      </MenuItem>}
                    {job._links['delete job'] &&
                      <MenuItem
                        className={classes.dropdownItem}
                        dense
                        onClick={deleteJob}>
                        <i className='fas fa-trash' />
                        <span>Delete</span>
                      </MenuItem>}
                  </Menu>
                  <IconButton
                    className={classes.button}
                    aria-label='actions-menu'
                    onClick={handleOpenMenu}
                    data-testid="actions-menu"
                    title="More">
                    <MoreVert className={classes.largeIcon} />
                </IconButton>
              </>)}
            </div>
        </div>
  )
}

export const JobRowWithContext = bindContexts(JobRow, [
  Auth0Context,
])

export default JobRow
